import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  createContext,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  getAllAlmaxStudents,
  getAllConstants,
} from '../../../services/apiService';
import { toast } from 'react-toastify';
import { getAllAlmaXStudentsFilters } from './utils/column-filters.js';
import { columnDefs, defaultColDef } from './utils/defination-config.jsx';
import { PageHeader } from '../../../extras/PageLayout';
import useUrlFilters from '../../../extras/useUrlFilters';

export const AlmaXStudentsRefreshContext = createContext();

const pageSize = 10;

// Searchable Cohort Dropdown Component
const SearchableCohortDropdown = ({ cohortOptions, value, onChange, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Update display value when value prop changes
  useEffect(() => {
    if (value) {
      const selectedCohort = cohortOptions.find(
        (cohort) => cohort.id === value
      );
      setDisplayValue(selectedCohort ? selectedCohort.name : '');
      setSearchTerm(selectedCohort ? selectedCohort.name : '');
    } else {
      setDisplayValue('');
      setSearchTerm('');
    }
  }, [value, cohortOptions]);

  // Filter cohorts based on search term
  const filteredCohorts = useMemo(() => {
    if (!searchTerm) return cohortOptions;
    return cohortOptions.filter((cohort) =>
      cohort.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [cohortOptions, searchTerm]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset search term to selected value if no selection made
        if (value) {
          const selectedCohort = cohortOptions.find(
            (cohort) => cohort.id === value
          );
          setSearchTerm(selectedCohort ? selectedCohort.name : '');
          setDisplayValue(selectedCohort ? selectedCohort.name : '');
        } else {
          setSearchTerm('');
          setDisplayValue('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, cohortOptions]);

  const handleInputChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setDisplayValue(newSearchTerm);
    setIsOpen(true);

    // If search term is empty, clear the selection
    if (!newSearchTerm) {
      onChange({ target: { name, value: '' } });
    }
  };

  const handleCohortSelect = (cohort) => {
    setSearchTerm(cohort.name);
    setDisplayValue(cohort.name);
    setIsOpen(false);
    onChange({ target: { name, value: cohort.id } });
  };

  const handleClearSelection = () => {
    setSearchTerm('');
    setDisplayValue('');
    setIsOpen(false);
    onChange({ target: { name, value: '' } });
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className='relative' ref={dropdownRef}>
      <div className='relative'>
        <input
          ref={inputRef}
          type='text'
          style={{ border: '1px solid var(--border-main, #D1D5DB)', borderRadius: 4, padding: '8px 12px', width: '100%', paddingRight: 32, backgroundColor: 'var(--input-background, #fff)', color: 'var(--text-primary, #111827)', fontSize: 14 }}
          placeholder='Search cohorts...'
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
        />
        <div className='absolute right-2 top-1/2  transform -translate-y-1/2 flex items-center gap-1'>
          {value && (
            <button
              type='button'
              onClick={handleClearSelection}
              style={{ fontSize: '25px', color: 'var(--text-muted, #9CA3AF)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              title='Clear selection'
            >
              ×
            </button>
          )}

          <button
            type='button'
            onClick={() => setIsOpen(!isOpen)}
            style={{ color: 'var(--text-muted, #9CA3AF)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}
          >
            ▼
          </button>
        </div>
      </div>

      {isOpen && (
        <div style={{ position: 'absolute', zIndex: 50, width: '100%', marginTop: 4, backgroundColor: 'var(--card-background, #fff)', border: '1px solid var(--border-main, #D1D5DB)', borderRadius: 6, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', maxHeight: 240, overflowY: 'auto' }}>
          {filteredCohorts.length > 0 ? (
            <>
              <div
                style={{ padding: '8px 12px', cursor: 'pointer', color: 'var(--text-secondary, #6B7280)', borderBottom: '1px solid var(--border-light, rgba(0,0,0,0.07))' }}
                onClick={handleClearSelection}
              >
                Clear Selection
              </div>
              {filteredCohorts.map((cohort) => (
                <div
                  key={cohort.id}
                  style={{
                    padding: '8px 12px', cursor: 'pointer',
                    backgroundColor: value === cohort.id ? 'var(--primary-light, #EEF2FF)' : 'transparent',
                    color: value === cohort.id ? 'var(--primary-main, #4338CA)' : 'var(--text-primary, #111827)',
                  }}
                  onClick={() => handleCohortSelect(cohort)}
                >
                  {cohort.name}
                </div>
              ))}
            </>
          ) : (
            <div style={{ padding: '8px 12px', color: 'var(--text-muted, #9CA3AF)' }}>No cohorts found</div>
          )}
        </div>
      )}
    </div>
  );
};

const AllAlmaXStudents = () => {
  const [gridApi, setGridApi] = useState(null);
  const [cohortOptions, setCohortOptions] = useState([]);

  const DEFAULT_FILTERS = {
    email: '',
    cohort: '',
    course: '',
    tag: '',
  };

  const {
    filters: filterInputs,
    setFilters: setFilterInputs,
    clearFilters: clearUrlFilters,
  } = useUrlFilters({ defaultFilters: DEFAULT_FILTERS });

  const [refresh, setRefresh] = useState(false);
  let lastRequestedPage = null;

  const filtersConfig = getAllAlmaXStudentsFilters();

  const getServerSideDatasource = () => {
    return {
      getRows: async (params) => {
        const { startRow } = params.request;
        const currentPage = Math.floor(startRow / pageSize) + 1;
        if (currentPage === lastRequestedPage) {
          return;
        }
        lastRequestedPage = currentPage;
        try {
          const response = await getAllAlmaxStudents(currentPage, {
            email: filterInputs.email,
            almaXCohort: filterInputs.cohort,
            course: filterInputs.course,
            tag: filterInputs.tag,
          });
          const allData = response?.data || [];
          const totalCount = response?.totalRecords || response?.totalCount;
          params.successCallback(allData, totalCount);
        } catch (err) {
          toast.error('Failed to fetch data');
          params.failCallback();
        }
      },
    };
  };

  const onGridReady = useCallback(
    (params) => {
      setGridApi(params.api);
      const datasource = getServerSideDatasource();
      params.api.setServerSideDatasource(datasource);
    },
    [
      filterInputs.email,
      filterInputs.cohort,
      filterInputs.course,
      filterInputs.tag,
    ]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilter = () => {
    if (gridApi) {
      const newDatasource = getServerSideDatasource();
      gridApi.setServerSideDatasource(newDatasource);
    }
  };

  const handleClearFilter = () => {
    clearUrlFilters();
    setRefresh(!refresh);
  };

  const refreshGridData = useCallback(() => {
    if (gridApi) {
      const newDatasource = getServerSideDatasource();
      gridApi.setServerSideDatasource(newDatasource);
    }
  }, [gridApi, filterInputs]);

  const agGridColumnDefs = useMemo(() => {
    const baseCols = columnDefs().filter((col) => col.field !== 'actions');
    return baseCols;
  }, []);

  useEffect(() => {
    if (gridApi) {
      const newDatasource = getServerSideDatasource();
      gridApi.setServerSideDatasource(newDatasource);
    }
  }, [refresh]);

  useEffect(() => {
    async function fetchConstants() {
      try {
        const res = await getAllConstants();
        const cohorts = (res?.allCohorts || [])
          .sort((a, b) => a.cohortName.localeCompare(b.cohortName))
          .map((c) => ({
            id: c.cohortId,
            name: c.cohortName,
          }));
        setCohortOptions(cohorts);
      } catch (e) {
        setCohortOptions([]);
      }
    }
    fetchConstants();
  }, []);

  return (
    <AlmaXStudentsRefreshContext.Provider value={{ refreshGridData }}>
      <div>
        {/* Page header */}
        <PageHeader
          title="All AlmaX Students"
          subtitle="Manage AlmaX student enrollments"
        />

      {/* Filter bar */}
      <div style={{ padding: '0 20px', marginBottom: 16 }}>
        <div
          style={{
            backgroundColor: 'var(--card-background, #fff)',
            borderRadius: 12,
            border: '1px solid var(--border-light, rgba(0,0,0,0.07))',
            boxShadow: 'var(--shadow-sm, 0 1px 6px rgba(0,0,0,0.05))',
            padding: '16px 20px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'flex-end',
          }}
        >
          {filtersConfig.map((filter) => (
            <div
              style={{ display: 'flex', flexDirection: 'column', minWidth: filter.minWidth || 140 }}
              key={filter.name}
            >
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary, #6B7280)', marginBottom: 4 }}>
                {filter.label}
              </label>

              {filter.type === 'text' ? (
                <input
                  style={{
                    border: '1px solid var(--border-light, #E5E7EB)',
                    borderRadius: 6,
                    padding: '7px 12px',
                    fontSize: 13,
                    color: 'var(--text-primary, #111827)',
                    outline: 'none',
                    backgroundColor: 'var(--input-background, #fff)',
                  }}
                  type='text'
                  name={filter.name}
                  placeholder={filter.placeholder}
                  value={filterInputs[filter.name]}
                  onChange={handleInputChange}
                />
              ) : (
                <select
                  style={{
                    border: '1px solid var(--border-light, #E5E7EB)',
                    borderRadius: 6,
                    padding: '7px 12px',
                    fontSize: 13,
                    color: 'var(--text-primary, #111827)',
                    outline: 'none',
                    backgroundColor: 'var(--input-background, #fff)',
                  }}
                  name={filter.name}
                  value={filterInputs[filter.name]}
                  onChange={handleInputChange}
                >
                  {filter.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}

          {/* Searchable Cohort Dropdown */}
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary, #6B7280)', marginBottom: 4 }}>
              Almax Cohort
            </label>
            <SearchableCohortDropdown
              cohortOptions={cohortOptions}
              value={filterInputs.cohort}
              onChange={handleInputChange}
              name='cohort'
            />
          </div>

          {/* Filter buttons */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#fff',
                backgroundColor: 'var(--primary-main, #4F46E5)',
                border: 'none',
                borderRadius: 4,
                padding: '8px 16px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
              }}
              onClick={handleApplyFilter}
            >
              Apply Filter
            </button>
            <button
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text-primary, #374151)',
                backgroundColor: 'var(--input-background, #fff)',
                border: '1px solid var(--border-light, #E5E7EB)',
                borderRadius: 4,
                padding: '8px 16px',
                cursor: 'pointer',
              }}
              onClick={handleClearFilter}
            >
              Clear Filter
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: '0 20px 24px' }}>
        <div
          style={{
            backgroundColor: 'var(--card-background, #fff)',
            borderRadius: 16,
            border: '1px solid var(--border-light, rgba(0,0,0,0.07))',
            boxShadow: 'var(--shadow-sm, 0 1px 6px rgba(0,0,0,0.05))',
            overflow: 'hidden',
          }}
        >
          <div
            className='ag-theme-alpine'
            style={{ height: 520, width: '100%' }}
          >
            <AgGridReact
              columnDefs={agGridColumnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              tooltipShowDelay={500}
              tooltipHideDelay={2000}
              enableBrowserTooltips={true}
              rowHeight={52}
              rowModelType='serverSide'
              serverSideStoreType='partial'
              animateRows={true}
              serverSideInfiniteScroll={true}
              pagination={true}
              paginationPageSize={pageSize}
              cacheBlockSize={pageSize}
              suppressPaginationPanel={false}
              domLayout='normal'
            />
          </div>
        </div>
      </div>
    </div>
    </AlmaXStudentsRefreshContext.Provider>
  );
};

export default AllAlmaXStudents;
